import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { format } from 'date-fns';

// Helper to calculate time based on Y coordinate within a slot
const calculateTimeFromY = (baseTime, y, slotTop, slotHeight, durationMinutes = 30) => {
  const relativeY = y - slotTop;
  const percentageY = Math.max(0, Math.min(1, relativeY / slotHeight));
  const minutesToAdd = Math.round(percentageY * durationMinutes);
  const calculatedTime = new Date(baseTime);
  calculatedTime.setMinutes(calculatedTime.getMinutes() + minutesToAdd);
  return calculatedTime;
};

// Helper to format time for display
const formatTimeForDisplay = (date) => {
  if (!date) return '';
  try {
    return format(date, 'HH:mm');
  } catch (e) {
    console.error("Error formatting time:", e);
    return '';
  }
};

const CalendarSlot = ({ date, time, onTaskDrop, children }) => {
  const slotRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropPosition, setDropPosition] = useState(50); // Default to middle (50%)
  const [isSlotEmpty, setIsSlotEmpty] = useState(true);
  
  // State for task creation by dragging
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null); // Info about drag start { y, percentY, slotTop, slotHeight, calendarRect, startTimeDate }
  const [dragCurrentY, setDragCurrentY] = useState(null); // Current mouse Y during drag for preview calculation

  // Check if slot is empty on mount and when children change
  useEffect(() => {
    if (slotRef.current) {
      // Check if there are any task elements in this slot
      const hasTaskElements = slotRef.current.querySelector('.rbc-event-content') !== null;
      setIsSlotEmpty(!hasTaskElements);
    }
  }, [children]);

  // Add direct event listeners for more reliable drag detection
  useEffect(() => {
    if (!slotRef.current) return;
    
    // Define a dragenter event handler for the document
    const handleGlobalDragEnter = (e) => {
      if (!slotRef.current) return;
      
      // Check if the drag is entering our slot element
      if (slotRef.current.contains(e.target)) {
        e.preventDefault();
        setIsDragOver(true);
        updateDropPosition(e);
      }
    };

    // Define event handlers for the slot element
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
      updateDropPosition(e);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Only set to false if leaving the element, not entering a child
      if (!slotRef.current.contains(e.relatedTarget)) {
        setIsDragOver(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      
      try {
        const data = JSON.parse(e.dataTransfer.getData('application/json'));
        
        if (data && data.taskId) {
          let exactTime = time ? format(time, 'HH:mm') : null;
          
          if (time) {
            const rect = slotRef.current.getBoundingClientRect();
            const relativeY = e.clientY - rect.top;
            const percentageY = relativeY / rect.height;
            
            const slotDate = new Date(time);
            const minutes = Math.floor(percentageY * 30);
            slotDate.setMinutes(slotDate.getMinutes() + minutes);
            
            exactTime = format(slotDate, 'HH:mm');
          }
          
          onTaskDrop(data.taskId, date, exactTime);
        }
      } catch (error) {
        console.error('Error parsing drag data:', error);
      }
    };

    // Helper function to update drop position indicator
    const updateDropPosition = (e) => {
      if (!time || !slotRef.current) return;
      
      const rect = slotRef.current.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      const percentageY = (relativeY / rect.height) * 100;
      setDropPosition(Math.max(0, Math.min(percentageY, 100))); // Clamp between 0-100%
    };
    
    // Define handlers for task creation drag
    const handleMouseDown = (e) => {
      if (!isSlotEmpty || e.button !== 0) return;
      e.preventDefault();
      
      //console.log("[CalendarSlot] handleMouseDown triggered");
      
      const rect = slotRef.current.getBoundingClientRect();
      const initialTime = calculateTimeFromY(time || date, e.clientY, rect.top, rect.height);
      //console.log("[CalendarSlot] Calculated initialTime:", initialTime);
      
      const calendarElement = slotRef.current.closest('.rbc-time-view, .rbc-month-view');
      const calendarRect = calendarElement ? calendarElement.getBoundingClientRect() : null;
      
      setIsDragging(true);
      setDragStart({
        y: e.clientY,
        slotTop: rect.top,
        slotHeight: rect.height,
        calendarRect: calendarRect,
        startTimeDate: initialTime // Store the actual Date object here
      });
      setDragCurrentY(e.clientY); // Initialize current Y
      
      // Minimal global state needed, maybe just for drop target info if necessary
      window.__calendarDragInfo = { startSlot: slotRef.current };
    };
    
    // Global mouse move handler to track dragging outside the initial slot
    const handleGlobalMouseMove = (e) => {
      if (!isDragging || !dragStart) return;
      // //console.log("[CalendarSlot] handleGlobalMouseMove triggered");
      e.preventDefault();
      setDragCurrentY(e.clientY); // Update current Y position
    };
    
    const handleMouseUp = (e) => {
      if (!isDragging || !dragStart) {
        if (isDragging) {
          // Reset state if drag ended unexpectedly
          setIsDragging(false);
          setDragStart(null);
          setDragCurrentY(null);
          delete window.__calendarDragInfo;
        }
        return;
      }
      //console.log("[CalendarSlot] handleMouseUp triggered");
      
      // Calculate final end time based on current mouse position
      const finalEndTime = calculateTimeFromY(
        time || date, // Base time of the slot where drag started
        e.clientY,    // Current mouse Y
        dragStart.slotTop,
        dragStart.slotHeight
      );
      
      // Normalize start/end times
      let startTime = new Date(dragStart.startTimeDate); // Get start time from dragStart
      let endTime = finalEndTime;
      if (startTime > endTime) {
        [startTime, endTime] = [endTime, startTime]; // Swap if needed
      }
      
      // Round times to nearest 15 minutes (optional, for cleaner UX)
      startTime.setMinutes(Math.round(startTime.getMinutes() / 15) * 15, 0, 0);
      endTime.setMinutes(Math.round(endTime.getMinutes() / 15) * 15, 0, 0);
      
      // Ensure minimum duration (e.g., 15 minutes)
      if (endTime.getTime() <= startTime.getTime()) {
         endTime = new Date(startTime.getTime() + 15 * 60000);
      }
      
      // Format times for API/Callback
      const startHour = startTime.getHours();
      const startMinute = startTime.getMinutes();
      const endHour = endTime.getHours();
      const endMinute = endTime.getMinutes();
      const formattedStartTime = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
      const formattedEndTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
      
      // Use the original slot's date for consistency
      const slotDate = window.__calendarDragInfo?.startSlot?.dataset.date || date; // Get date from original slot or prop
      
      //console.log(`[CalendarSlot] Calling onTaskDrop with:`, { taskId: null, date: slotDate, startTime: formattedStartTime, endTime: formattedEndTime });
      onTaskDrop(null, slotDate, formattedStartTime, formattedEndTime, 'New Task');
      
      // Reset drag state
      //console.log("[CalendarSlot] Resetting drag state");
      setIsDragging(false);
      setDragStart(null);
      setDragCurrentY(null);
      delete window.__calendarDragInfo;
    };

    // Add global document event listener to catch all drag enters
    document.addEventListener('dragenter', handleGlobalDragEnter);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleGlobalMouseMove);
    
    // Add specific slot event listeners
    const slotElement = slotRef.current;
    slotElement.addEventListener('dragover', handleDragOver);
    slotElement.addEventListener('dragleave', handleDragLeave);
    slotElement.addEventListener('drop', handleDrop);
    slotElement.addEventListener('mousedown', handleMouseDown);

    // Cleanup function
    return () => {
      document.removeEventListener('dragenter', handleGlobalDragEnter);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      if (slotElement) {
        slotElement.removeEventListener('dragover', handleDragOver);
        slotElement.removeEventListener('dragleave', handleDragLeave);
        slotElement.removeEventListener('drop', handleDrop);
        slotElement.removeEventListener('mousedown', handleMouseDown);
      }
    };
  }, [isDragging, dragStart, isSlotEmpty, time, date, onTaskDrop]);

  // Calculate preview times and position dynamically
  const getPreviewData = () => {
    if (!isDragging || !dragStart || dragCurrentY === null) {
      return null;
    }

    const currentEndTime = calculateTimeFromY(
      time || date,
      dragCurrentY,
      dragStart.slotTop,
      dragStart.slotHeight
    );

    let previewStartTime = new Date(dragStart.startTimeDate);
    let previewEndTime = currentEndTime;

    // Ensure start is before end for display
    if (previewStartTime > previewEndTime) {
      [previewStartTime, previewEndTime] = [previewEndTime, previewStartTime];
    }

    // Calculate position relative to the viewport
    const top = Math.min(dragStart.y, dragCurrentY);
    const bottom = Math.max(dragStart.y, dragCurrentY);
    const height = bottom - top;

    // Ensure minimum height for visibility
    const minHeight = 20;
    const displayHeight = Math.max(height, minHeight);
    const displayTop = height < minHeight ? top - (minHeight - height) / 2 : top;

    return {
      top: displayTop,
      height: displayHeight,
      startTime: formatTimeForDisplay(previewStartTime),
      endTime: formatTimeForDisplay(previewEndTime),
      // Adjust left/width based on the original slot's position if needed
      // This requires getting the slotRef's rect, potentially passing calendarRect from dragStart
      left: dragStart.calendarRect ? dragStart.calendarRect.left + (slotRef.current.getBoundingClientRect().left - dragStart.calendarRect.left) : slotRef.current.getBoundingClientRect().left,
      width: slotRef.current.getBoundingClientRect().width,
    };
  };

  const previewData = getPreviewData();

  return (
    <div 
      ref={slotRef} 
      className={`calendar-slot rbc-calendar-slot relative transition-colors duration-200 ${isDragOver ? 'calendar-drag-over' : ''}`}
      style={{ 
        height: '100%', 
        minHeight: '30px',
        position: 'relative',
        boxSizing: 'border-box',
        padding: 0,
        width: '100%',
        overflow: 'visible', // Allow indicator to overflow
        zIndex: isDragOver ? 100 : 1, // Raise z-index when dragging over
        cursor: isSlotEmpty ? 'cell' : 'default' // Use cell cursor for empty slots
      }}
      data-date={time ? format(time, 'yyyy-MM-dd') : format(date, 'yyyy-MM-dd')} // Store date for drop handling
    >
      {isDragOver && (
        <div 
          className="drop-indicator" 
          style={{ 
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${dropPosition}%`, 
            transform: 'translateY(-50%)',
            height: '3px',
            backgroundColor: '#3b82f6',
            zIndex: 200,
            pointerEvents: 'none',
            boxShadow: '0 0 4px rgba(59, 130, 246, 0.5)'
          }}
        />
      )}
      
      {/* Highlight empty slots for better UX */}
      {isSlotEmpty && (
        <div 
          className="slot-empty-indicator"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDragOver ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.03)',
            pointerEvents: 'none',
            borderRadius: '2px'
          }}
        />
      )}
      
      {children}
      
      {/* Drag-to-create Preview Element - Appended to body */}
      {isDragging && previewData && ReactDOM.createPortal(
        <div
          className="drag-preview"
          style={{
            position: 'absolute',
            top: `${previewData.top}px`,
            left: `${previewData.left}px`, // Position based on slot
            width: `${previewData.width}px`, // Match slot width
            height: `${previewData.height}px`,
            backgroundColor: 'rgba(59, 130, 246, 0.3)', // Semi-transparent blue
            border: '1px dashed rgba(37, 99, 235, 0.7)',
            borderRadius: '4px',
            zIndex: 1000, // High z-index to be on top
            pointerEvents: 'none', // Ignore mouse events
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between', // Push times to top and bottom
            alignItems: 'center',
            overflow: 'hidden',
            padding: '2px 4px', // Small padding
            boxSizing: 'border-box',
            fontSize: '10px', // Small font size
            color: '#1e3a8a', // Dark blue text
          }}
        >
          <span style={{ width: '100%', textAlign: 'center', fontWeight: 'bold' }}>{previewData.startTime}</span>
          {/* Add a flexible spacer if needed, or rely on justify-content */}
          {/* <div style={{ flexGrow: 1 }}></div> */}
          <span style={{ width: '100%', textAlign: 'center', fontWeight: 'bold' }}>{previewData.endTime}</span>
        </div>,
        document.body // Render directly into the body
      )}
    </div>
  );
};

export default CalendarSlot; 